import { z } from 'zod';

export const INVOICE_STATUSES = ['draft', 'open', 'partial', 'paid', 'void'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const money = z.string().regex(/^-?\d+(\.\d{1,4})?$/, 'must be a decimal string');
const nonNegMoney = z
  .string()
  .regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative money value');
const positiveMoney = nonNegMoney.refine((v) => Number(v) > 0, 'must be greater than zero');

export const invoiceLineInputSchema = z.object({
  accountId: z.string().uuid(), // revenue account credited
  description: z.string().max(500).nullable().optional(),
  quantity: nonNegMoney.default('1'),
  unitPrice: nonNegMoney.default('0'),
  locationId: z.string().uuid().nullable().optional(),
  classId: z.string().uuid().nullable().optional(),
});
export type InvoiceLineInput = z.infer<typeof invoiceLineInputSchema>;

export const createInvoiceInputSchema = z.object({
  /** Optional — an invoice can be issued without naming a customer (cash sale). */
  customerId: z.string().uuid().nullable().optional(),
  /**
   * Receivable account to debit. Omit and it is resolved from the chart, which
   * is right for a company with one A/R account. Name it explicitly when the
   * company has more than one — resolution matches on subtype, so two active
   * receivable accounts make the choice depend on row order.
   */
  arAccountId: z.string().uuid().nullable().optional(),
  /** Optional explicit number; auto-assigned (INV-00001…) when omitted. */
  invoiceNumber: z.string().trim().min(1).max(20).optional(),
  invoiceDate: isoDate,
  dueDate: isoDate,
  terms: z.string().max(100).nullable().optional(),
  memo: z.string().max(500).nullable().optional(),
  lines: z.array(invoiceLineInputSchema).min(1),
  /** Save without issuing — no journal entry is posted until the draft is issued. */
  asDraft: z.boolean().optional(),
  /**
   * Source system's invoice ID (external sync). When set, re-sending the same
   * externalId returns the existing invoice instead of creating a duplicate.
   */
  externalId: z.string().trim().min(1).max(100).optional(),
});
export type CreateInvoiceInput = z.infer<typeof createInvoiceInputSchema>;

/** Edit an open (unpaid) invoice. The invoice number is fixed; everything else may change. */
export const updateInvoiceInputSchema = z.object({
  customerId: z.string().uuid().nullable().optional(),
  invoiceDate: isoDate,
  dueDate: isoDate,
  terms: z.string().max(100).nullable().optional(),
  memo: z.string().max(500).nullable().optional(),
  lines: z.array(invoiceLineInputSchema).min(1),
});
export type UpdateInvoiceInput = z.infer<typeof updateInvoiceInputSchema>;

export const recordInvoicePaymentInputSchema = z.object({
  paymentDate: isoDate,
  amount: positiveMoney,
  reference: z.string().max(100).nullable().optional(),
  /** GL account to debit; defaults to Undeposited Funds when omitted. */
  depositAccountId: z.string().uuid().nullable().optional(),
  /** Source system's payment ID (external sync) — makes re-sends idempotent. */
  externalId: z.string().trim().min(1).max(100).optional(),
});
export type RecordInvoicePaymentInput = z.infer<typeof recordInvoicePaymentInputSchema>;

export const voidInvoiceInputSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type VoidInvoiceInput = z.infer<typeof voidInvoiceInputSchema>;

/** One invoice and how much of a customer payment is applied to it. */
export const paymentAllocationSchema = z.object({
  invoiceId: z.string().uuid(),
  amount: positiveMoney,
});
export type PaymentAllocation = z.infer<typeof paymentAllocationSchema>;

/** A single customer payment applied across one or more outstanding invoices. */
export const receiveCustomerPaymentInputSchema = z.object({
  customerId: z.string().uuid(),
  paymentDate: isoDate,
  /** GL account to debit; defaults to Undeposited Funds when omitted. */
  depositAccountId: z.string().uuid().nullable().optional(),
  reference: z.string().max(100).nullable().optional(),
  allocations: z.array(paymentAllocationSchema).min(1),
});
export type ReceiveCustomerPaymentInput = z.infer<typeof receiveCustomerPaymentInputSchema>;

export interface ReceiveCustomerPaymentResult {
  invoices: InvoiceWithDetails[];
  journalEntryId: string;
  total: string;
}

export const listInvoicesQuerySchema = z.object({
  customerId: z.string().uuid().optional(),
  status: z.enum(INVOICE_STATUSES).optional(),
  /** Look up an externally synced invoice by its source-system ID. */
  externalId: z.string().trim().min(1).max(100).optional(),
});
export type ListInvoicesQuery = z.infer<typeof listInvoicesQuerySchema>;

export interface Invoice {
  id: string;
  companyId: string;
  /** Null when the invoice was issued without a customer. */
  customerId: string | null;
  customerName: string | null;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: InvoiceStatus;
  /** Derived: open/partial and past the due date. */
  isOverdue: boolean;
  arAccountId: string;
  subtotal: string;
  total: string;
  amountPaid: string;
  outstandingAmount: string;
  memo: string | null;
  terms: string | null;
  journalEntryId: string | null;
  voidReason: string | null;
  voidedAt: string | null;
  /** Source system's invoice ID when pushed by an external sync; null otherwise. */
  externalId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceLine {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  description: string | null;
  quantity: string;
  unitPrice: string;
  amount: string;
}

export interface InvoicePayment {
  id: string;
  paymentDate: string;
  amount: string;
  depositAccountId: string;
  depositAccountCode: string;
  depositAccountName: string;
  reference: string | null;
  journalEntryId: string | null;
  createdAt: string;
}

export interface InvoiceWithDetails extends Invoice {
  lines: InvoiceLine[];
  payments: InvoicePayment[];
}

export interface NextInvoiceNumber {
  invoiceNumber: string;
}

// ── Customer center ─────────────────────────────────────────────────────────

export interface CustomerCenterRow {
  customerId: string;
  name: string;
  email: string | null;
  invoiceCount: number;
  openCount: number;
  overdueCount: number;
  totalInvoiced: string;
  totalPaid: string;
  outstanding: string;
  overdue: string;
}

export interface CustomerCenterResponse {
  rows: CustomerCenterRow[];
  totals: {
    invoiceCount: number;
    totalInvoiced: string;
    outstanding: string;
    overdue: string;
  };
}

// ── A/R aging ───────────────────────────────────────────────────────────────

export interface ArAgingRow {
  customerId: string;
  name: string;
  current: string;
  d1_30: string;
  d31_60: string;
  d61_90: string;
  d91_plus: string;
  total: string;
}

export interface ArAgingResponse {
  asOfDate: string;
  rows: ArAgingRow[];
  totals: {
    current: string;
    d1_30: string;
    d31_60: string;
    d61_90: string;
    d91_plus: string;
    total: string;
  };
}
